import { useEffect } from 'react';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { resolveEmblem } from '@/constants/emblems';

/** Default browser-tab title when no unit is active. */
const DEFAULT_TITLE = 'VTAK';

/** Ensure a `<link rel="icon">` exists and return it. */
function ensureFaviconLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  return link;
}

/**
 * Brand the browser tab with the active unit: title becomes "1 ROPS · VTAK"
 * and the favicon becomes the unit emblem, so the whole session reads as that
 * unit's instance. Restores the previous favicon when the unit clears.
 */
export function useUnitBranding(): void {
  const shortName = useWorkspaceStore((s) => s.workspace?.shortName ?? null);
  const emblem = useWorkspaceStore((s) => s.emblem);

  useEffect(() => {
    document.title = shortName ? `${shortName} · ${DEFAULT_TITLE}` : DEFAULT_TITLE;
  }, [shortName]);

  useEffect(() => {
    const url = resolveEmblem(emblem);
    if (!url) return;
    const link = ensureFaviconLink();
    const previous = link.href;
    link.href = url;
    return () => {
      link.href = previous;
    };
  }, [emblem]);
}
